import { BarChartDatum } from "components/molecules/Charts/BarChart";
import { buildingTypeOptions, CarbonPolicyCostChartGroup } from "utils/constants";

export interface CarbonPolicyBarChartData {
    barChartData: BarChartDatum[];
    cumulativeCarbonFines: number;
    cumulativeCarbonFineSavings: number;
}

export interface CarbonPolicyDatum {
    policyId: number;
    policyName: string;
    city: string;
    stateProvince: string;
    buildingArchetype: string;
    propertyName: string;
    fundName: string;
    grossFloorArea: number;
    baselineCost: number;
    planCost: number;
}

type CarbonPolicyAggregator = (carbonPolicyData: CarbonPolicyDatum[]) => CarbonPolicyBarChartData;

function carbonPolicyAggregator(carbonPolicyData: CarbonPolicyDatum[]): CarbonPolicyBarChartData {
    const groupedData = Object.groupBy(carbonPolicyData, ({ policyName }) => policyName);
    const aggregatedData = aggregateGroupedData(groupedData);
    return summarizeAggregatedData(aggregatedData);
}

function buildingArchetypeAggregator(carbonPolicyData: CarbonPolicyDatum[]): CarbonPolicyBarChartData {
    const groupedData = Object.groupBy(carbonPolicyData, ({ buildingArchetype }) => {
        const humanReadable = buildingTypeOptions.find(({ key }) => key === buildingArchetype)?.label ?? "undefined";
        return humanReadable;
    });
    const aggregatedData = aggregateGroupedData(groupedData);
    return summarizeAggregatedData(aggregatedData);
}

function regionAggregator(carbonPolicyData: CarbonPolicyDatum[]): CarbonPolicyBarChartData {
    const groupedData = Object.groupBy(carbonPolicyData, ({ stateProvince }) => stateProvince);
    const aggregatedData = aggregateGroupedData(groupedData);
    return summarizeAggregatedData(aggregatedData);
}

function propertyAggregator(carbonPolicyData: CarbonPolicyDatum[]): CarbonPolicyBarChartData {
    const groupedData = Object.groupBy(carbonPolicyData, ({ propertyName }) => propertyName ?? "no property");
    const aggregatedData = aggregateGroupedData(groupedData);
    return summarizeAggregatedData(aggregatedData);
}

function fundAggregator(carbonPolicyData: CarbonPolicyDatum[]): CarbonPolicyBarChartData {
    const groupedData = Object.groupBy(carbonPolicyData, ({ fundName }) => fundName ?? "no fund");
    const aggregatedData = aggregateGroupedData(groupedData);
    return summarizeAggregatedData(aggregatedData);
}

export function getCarbonPolicyAggregator(
    carbonPolicyCostChartGroup: CarbonPolicyCostChartGroup
): CarbonPolicyAggregator {
    const groupToAggregator = {
        carbonPolicy: carbonPolicyAggregator,
        property: propertyAggregator,
        fund: fundAggregator,
        region: regionAggregator,
        buildingArchetype: buildingArchetypeAggregator,
    };
    return groupToAggregator[carbonPolicyCostChartGroup];
}

function aggregateGroupedData(groupedData: Partial<Record<string, CarbonPolicyDatum[]>>): BarChartDatum[] {
    return Object.entries(groupedData).flatMap(([groupName, groupData]) => {
        if (groupData === undefined || groupData.length === 0) return [];

        const baselineCost = groupData.reduce((acc, groupDatum) => acc + groupDatum.baselineCost, 0);
        const planCost = groupData.reduce((acc, groupDatum) => acc + groupDatum.planCost, 0);

        return [
            formatData({
                title: groupName,
                baselineCost,
                planCost,
            }),
        ];
    });
}

function summarizeAggregatedData(barChartData: BarChartDatum[]): CarbonPolicyBarChartData {
    const cumulativeCarbonFines = barChartData.reduce(
        (acc, bcd) =>
            typeof bcd.carbonFinesSaveable === "number" && typeof bcd.carbonFinesUnsaveable === "number"
                ? acc + bcd.carbonFinesSaveable + bcd.carbonFinesUnsaveable
                : acc,
        0
    );
    const cumulativeCarbonFineSavings = barChartData.reduce(
        (acc, bcd) => (typeof bcd.carbonFinesSaveable === "number" ? acc + bcd.carbonFinesSaveable : acc),
        0
    );
    return {
        barChartData,
        cumulativeCarbonFines,
        cumulativeCarbonFineSavings,
    };
}

interface UnformattedDatum {
    title: string;
    baselineCost: number;
    planCost: number;
}

export const dataKeys = ["carbonFinesUnsaveable", "carbonFinesSaveable"];
export const titleKey = "carbonPolicy";
export const tooltipKeys = ["cumulativeCarbonFines", "cumulativeCarbonFineSavings"];

function formatData(data: UnformattedDatum): BarChartDatum {
    return {
        carbonPolicy: data.title,
        carbonFinesUnsaveable: data.planCost, // portion of fines that can't be saved
        carbonFinesSaveable: data.baselineCost - data.planCost, // portion of fines that can be saved
    };
}
